import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  IconChevronDown,
  IconChevronLeft,
  IconCirclePlusFilled,
  IconPillOff,
  IconShare3,
  IconTrash,
} from "@tabler/icons-react";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import emptyCardStyles from "@/components/ui/organisms/MEmptyCard.module.css";
import MLoader from "@/components/ui/atoms/MLoader";
import MBadge from "@/components/ui/atoms/MBadge";
import MDropDownDrawer, {
  DropdownItem,
} from "@/components/ui/molecules/MDropDownDrawer";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { triggerNativePillboxShoppingListShare } from "@/utils/nativeBridge";
import { getMemberInfo } from "@/hooks/useMembers";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";
import { usePillbox } from "@/hooks/usePillbox";
import pillboxService from "@/services/pillbox.service";
import { RootState } from "@/store";
import { MedicineTimingRuleType, PillboxItem } from "@/types/pillbox";
import { formatMedicineTitle } from "./utils/formatMedicine";
import AddFromPillboxDrawer from "./components/AddFromPillboxDrawer";
import RemoveFromShoppingListModal from "./components/RemoveFromShoppingListModal";
import styles from "./ShoppingList.module.css";

const ShoppingList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  // Forwards the List/Schedule tab the landing screen was on before
  // navigating here (see PillboxLanding's quick-action onClick), so going
  // back restores it instead of always landing on the default Schedule tab.
  // Explicit destination rather than navigate(-1) — shopping list is only
  // ever reached from the Pillbox landing screen's quick actions, so this
  // is always the correct target, and it carries the current fromView
  // through explicitly rather than depending on whatever the browser's
  // history stack happens to already hold for that entry.
  const handleBackToLanding = () => {
    const fromView = (
      location.state as { fromView?: "schedule" | "list" } | null
    )?.fromView;
    navigate("/pillbox", { state: { viewMode: fromView } });
  };
  // Shopping list is a cross-family, multi-member view — "You" must mean
  // the actual logged-in account holder, not whichever family member
  // profile happens to be currently selected in the profile picker.
  // getMemberInfo() alone returns the *selected* member (selectedMember ||
  // member), so if you last switched to viewing e.g. "Atam" and never
  // switched back, every "is this mine" check below would incorrectly
  // treat Atam as "You" — which is exactly what caused Atam's item to be
  // merged into the "You" group and Atam's own group to disappear.
  const memberInfo = getMemberInfo(true);
  const loggedInMemberId = memberInfo.id || "";
  // AddFromPillboxDrawer needs the raw (snake_case) Member shape to render
  // an avatar the same way MProfilePicker does — getMemberInfo() returns a
  // differently-shaped (camelCase) projection that doesn't have first_name/
  // last_name.
  const loggedInMember = useSelector((state: RootState) => state.auth.member);
  const { managedMembers, fetchManagedMembers } = useManagedMembersContext();
  const { fetchShoppingList, downloadShoppingListPdf } =
    usePillbox(loggedInMemberId);

  const [items, setItems] = useState<PillboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [pendingRemoveItem, setPendingRemoveItem] =
    useState<PillboxItem | null>(null);
  const [memberFilter, setMemberFilter] = useState<string>("ALL");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [addFromPillboxOpen, setAddFromPillboxOpen] = useState(false);

  useEffect(() => {
    if (!managedMembers || managedMembers.length === 0) {
      fetchManagedMembers();
    }
  }, [managedMembers, fetchManagedMembers]);

  const loadShoppingList = useCallback(() => {
    setLoading(true);
    return fetchShoppingList()
      .then((data) => setItems(data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadShoppingList();
  }, [loadShoppingList]);

  const memberName = useCallback(
    (memberId: string) => {
      if (memberId === loggedInMemberId) return t("pillbox.shoppingList.you");
      const match = managedMembers.find(
        (member: { id: string }) => member.id === memberId
      );
      return (
        match?.nickname ||
        match?.first_name ||
        t("pillbox.shoppingList.familyMember")
      );
    },
    [managedMembers, loggedInMemberId, t]
  );

  const groups = useMemo(() => {
    const map = new Map<string, PillboxItem[]>();
    for (const item of items) {
      if (!map.has(item.memberId)) map.set(item.memberId, []);
      map.get(item.memberId)!.push(item);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === loggedInMemberId) return -1;
      if (b === loggedInMemberId) return 1;
      return memberName(a).localeCompare(memberName(b));
    });
  }, [items, loggedInMemberId, memberName]);

  const filterOptions: DropdownItem[] = useMemo(() => {
    const options: DropdownItem[] = [
      { value: "ALL", label: t("pillbox.shoppingList.filterAll") },
    ];
    for (const [memberId] of groups) {
      options.push({ value: memberId, label: memberName(memberId) });
    }
    return options;
  }, [groups, memberName, t]);

  const visibleGroups = useMemo(
    () =>
      memberFilter === "ALL"
        ? groups
        : groups.filter(([memberId]) => memberId === memberFilter),
    [groups, memberFilter]
  );

  const handleShare = async () => {
    if (items.length === 0 || sharing) return;
    setSharing(true);
    try {
      // 1. Native webview: ask the shell to fetch the PDF and present the OS
      //    share sheet — same path as vitals/lab-insights share.
      const handed = triggerNativePillboxShoppingListShare({
        memberName: loggedInMember.nickname || loggedInMember.first_name || "",
      });
      if (handed) return;

      // 2. Web fallback: download the PDF directly.
      const blob = await downloadShoppingListPdf();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "pillbox-shopping-list.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.shoppingList.shareError"),
      });
    } finally {
      setSharing(false);
    }
  };

  const confirmRemove = async () => {
    const item = pendingRemoveItem;
    if (!item || removingId) return;
    setRemovingId(item.id);
    try {
      await pillboxService.toggleRefill(
        item.memberId,
        item.itemType,
        item.id,
        false
      );
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setPendingRemoveItem(null);
      showToast({
        variant: "success",
        message: t("pillbox.shoppingList.removedToast", {
          name: formatMedicineTitle(item),
        }),
      });
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.shoppingList.removeError"),
      });
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.loaderWrap}>
        <MLoader />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.headerCard}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackToLanding}
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
          <h1 className={styles.title}>{t("pillbox.shoppingList.title")}</h1>
          <button
            type="button"
            className={styles.headerShareButton}
            onClick={handleShare}
            disabled={items.length === 0 || sharing}
            aria-label={t("pillbox.shoppingList.share")}
          >
            <IconShare3 size={24} stroke={1.5} />
          </button>
        </div>
        <p className={styles.subtitle}>{t("pillbox.shoppingList.subtitle")}</p>
      </div>

      <div className={styles.body}>
        <div className={styles.addFromPillboxRow}>
          <button
            type="button"
            className={styles.addFromPillboxButton}
            onClick={() => setAddFromPillboxOpen(true)}
          >
            <IconCirclePlusFilled size={16} />
            {t("pillbox.shoppingList.addFromPillbox")}
          </button>
        </div>

        {items.length === 0 ? (
          <MEmptyCard
            className={`${emptyCardStyles.shoppingListVariant} ${styles.emptyCard}`}
            icon={<IconPillOff size={64} stroke={1.5} color="#AD95D1" />}
            primaryTitle={t("pillbox.shoppingList.emptyTitle")}
            secondaryTitle={t("pillbox.shoppingList.emptySubtitle")}
          />
        ) : (
          <>
            <button
              type="button"
              className={styles.filterChip}
              onClick={() => setFilterDrawerOpen(true)}
            >
              {filterOptions.find((option) => option.value === memberFilter)
                ?.label ?? t("pillbox.shoppingList.filterAll")}
              <IconChevronDown size={12} />
            </button>

            {visibleGroups.length === 0 ? (
              <p className={styles.filteredEmpty}>
                {t("pillbox.shoppingList.filteredEmpty")}
              </p>
            ) : (
              <div className={styles.groupList}>
                {visibleGroups.map(([memberId, groupItems]) => (
                  <div className={styles.group} key={memberId}>
                    <MBadge className={styles.groupBadge}>
                      {memberName(memberId)}
                    </MBadge>
                    <div className={styles.groupCard}>
                      {groupItems.map((item) => (
                        <div className={styles.itemRow} key={item.id}>
                          <span className={styles.itemName}>
                            {formatMedicineTitle(item)}
                            {item.timingRuleType ===
                              MedicineTimingRuleType.CONDITIONAL && (
                              <span className={styles.sosBadge}>
                                {t("pillbox.sos")}
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            className={styles.trashButton}
                            onClick={() => setPendingRemoveItem(item)}
                            disabled={removingId === item.id}
                            aria-label={t("pillbox.shoppingList.remove")}
                          >
                            <IconTrash size={20} stroke={1.5} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <MDropDownDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title={t("pillbox.shoppingList.filterTitle")}
        items={filterOptions}
        initialValue={memberFilter}
        onSelect={(value) => setMemberFilter(value as string)}
      />

      {loggedInMember && (
        <AddFromPillboxDrawer
          open={addFromPillboxOpen}
          onClose={() => setAddFromPillboxOpen(false)}
          loggedInMember={loggedInMember}
          managedMembers={managedMembers}
          onAdded={loadShoppingList}
        />
      )}

      <RemoveFromShoppingListModal
        open={!!pendingRemoveItem}
        itemName={
          pendingRemoveItem ? formatMedicineTitle(pendingRemoveItem) : ""
        }
        onClose={() => setPendingRemoveItem(null)}
        onConfirm={confirmRemove}
        loading={!!removingId && removingId === pendingRemoveItem?.id}
      />
    </div>
  );
};

export default ShoppingList;
